import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { LanguageCode, Translations } from '@/i18n/types'
import { defaultLanguage, translations } from '@/i18n/translations'

interface LanguageContextValue {
  language: LanguageCode
  setLanguage: (language: LanguageCode) => void
  t: Translations
}

const STORAGE_KEY = 'envprocess.language'

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

function getInitialLanguage(): LanguageCode {
  const saved = localStorage.getItem(STORAGE_KEY)
  if (saved === 'pt-BR' || saved === 'pt-PT' || saved === 'en') {
    return saved
  }
  return defaultLanguage
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(getInitialLanguage)

  const setLanguage = (nextLanguage: LanguageCode) => {
    setLanguageState(nextLanguage)
    localStorage.setItem(STORAGE_KEY, nextLanguage)
  }

  useEffect(() => {
    const content = translations[language]
    document.documentElement.lang = language
    document.title = content.meta.title

    const descriptionTag = document.querySelector('meta[name="description"]')
    if (descriptionTag) {
      descriptionTag.setAttribute('content', content.meta.description)
    }

    const ogTitleTag = document.querySelector('meta[property="og:title"]')
    if (ogTitleTag) {
      ogTitleTag.setAttribute('content', content.meta.title)
    }

    const ogDescriptionTag = document.querySelector('meta[property="og:description"]')
    if (ogDescriptionTag) {
      ogDescriptionTag.setAttribute('content', content.meta.description)
    }
  }, [language])

  const value = useMemo<LanguageContextValue>(() => {
    return {
      language,
      setLanguage,
      t: translations[language],
    }
  }, [language])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider')
  }
  return context
}
